import { addDays, addMonths } from 'date-fns'

function getPeriodType(periodNumber, tags) {
	return periodNumber == 1 && ['trial', 'in_intro', 'in_commitment'].some((item) => tags.includes(item))
		? 'trial'
		: 'normal'
}

export function convertPaltaSubscription(subscription, userId) {
	const { pricePoint, periodNumber, tags, currentPeriodStartAt, currentPeriodEndAt, canceledAt } = subscription
	const {
		nextTotalPrice,
		introTotalPrice,
		introPeriodValue,
		introPeriodType,
		nextPeriodValue,
		nextPeriodType,
		currencyCode,
		ident,
		commitmentPeriod,
	} = pricePoint
	const periodType = getPeriodType(periodNumber, tags)

	const phases = [
		{
			currency: currencyCode,
			price: parseFloat(nextTotalPrice),
			interval_duration: nextPeriodValue,
			interval_unit: nextPeriodType,
			iteration_count: null,
			remain_iterations: null,
			cancellation_fee_amount: null,
			cancellation_fee_price_id: null,
			product_price_id: ident,
			is_active: periodType === 'normal',
			phase_type: 'regular',
		},
	]

	if (tags.includes('in_commitment')) {
		phases.unshift({
			currency: currencyCode,
			price: parseFloat(introTotalPrice),
			interval_duration: introPeriodValue,
			interval_unit: introPeriodType,
			iteration_count: commitmentPeriod,
			remain_iterations: null,
			cancellation_fee_amount: 29.99,
			cancellation_fee_price_id: null,
			product_price_id: ident,
			is_active: true,
			phase_type: 'commitment',
		})
	} else if (periodType === 'trial') {
		phases.unshift({
			currency: currencyCode,
			price: parseFloat(introTotalPrice),
			interval_duration: introPeriodValue,
			interval_unit: introPeriodType,
			iteration_count: null,
			remain_iterations: null,
			cancellation_fee_amount: null,
			cancellation_fee_price_id: null,
			product_price_id: ident,
			is_active: true,
			phase_type: 'trial',
		})
	}

	return {
		is_fallback: true,
		customer_id: userId,
		product_id: ident,
		subscription_id: subscription.id,
		type: 'primary',
		status: 'active',
		start_at: currentPeriodStartAt,
		cancelled_at: null,
		cancelled: Boolean(canceledAt),
		is_expired: false,
		expired_at: null,
		is_paused: false,
		is_pause_allowed: true,
		is_overdue: false,
		paused_until_at: null,
		last_paused_at: null,
		current_period_start_at: currentPeriodStartAt,
		current_period_end_at: currentPeriodEndAt,
		is_intro_active: periodType === 'trial',
		billing_period_number: 1,
		remain_iterations: null,
		payment_method: 'card',
		provider: 'palta',
		is_upgraded: false,
		is_cancel_offer_allowed: true,
		has_cancel_offer: false,
		commitment_end_at: commitmentPeriod ? addMonths(new Date(currentPeriodStartAt), commitmentPeriod) : null,
		phases,
		scheduled: null,
	}
}

/**
 * Function to create a fake Palta subscription object based on the pricePoint
 *
 * @typedef {Object} Service
 * @property {string} featureIdent - Identifier for the feature.
 * @property {string} featureType - Type of the feature.
 * @property {number} quantity - Quantity of the feature.
 *
 * @typedef {Object} PricePoint
 * @property {number} storeType - The type of the store.
 * @property {string} ident - The identifier of the price point.
 * @property {string} name - The name of the price point.
 * @property {number} priority - The priority of the price point.
 * @property {string} currencyCode - The code of the currency.
 * @property {Object} parameters - Additional parameters for the price point.
 * @property {string} type - The type of the price point.
 * @property {string} nextBasePrice - The base price for the next period.
 * @property {number} nextPeriodValue - The value of the next period.
 * @property {string} nextPeriodType - The type of the next period.
 * @property {string} introBasePrice - The base price for the intro period.
 * @property {number} introPeriodValue - The value of the intro period.
 * @property {string} introPeriodType - The type of the intro period.
 * @property {string} nextDiscountPrice - The discount price for the next period.
 * @property {string} nextDiscountPercentage - The discount percentage for the next period.
 * @property {string} nextTotalPrice - The total price for the next period.
 * @property {string} introDiscountPrice - The discount price for the intro period.
 * @property {string} introDiscountPercentage - The discount percentage for the intro period.
 * @property {string} introTotalPrice - The total price for the intro period.
 * @property {Service[]} services - Array of service objects.
 *
 * @param {PricePoint} pricePoint - The pricing model for the subscription.
 *
 * @returns {Object} - The fake Palta subscription object.
 */

export function makePaltaSubscriptionByPricePoint(pricePoint) {
	const addPeriodFn = pricePoint.introPeriodType === 'day' ? addDays : addMonths
	const tags = ['in_intro']
	if (pricePoint.parameters.isTrial === 'true') {
		tags.push('trial')
	} else if (pricePoint.ident.includes('_comm_')) {
		tags.push('in_commitment')
	}

	return {
		pricePoint: {
			ident: pricePoint.ident,
			nextTotalPrice: pricePoint.nextTotalPrice,
			nextPeriodValue: pricePoint.nextPeriodValue,
			nextPeriodType: pricePoint.nextPeriodType,
			introTotalPrice: pricePoint.introTotalPrice,
			introPeriodValue: pricePoint.introPeriodValue,
			introPeriodType: pricePoint.introPeriodType,
			commitmentPeriod: null,
			currencyCode: pricePoint.currencyCode,
			services: [{ featureIdent: 'simple_premium', featureType: 'timebased', quantity: 1 }],
		},
		state: 'active',
		createdAt: new Date(),
		canceledAt: null,
		cancelType: null,
		cancelReason: null,
		currentPeriodStartAt: new Date(),
		currentPeriodEndAt: addPeriodFn(new Date(), pricePoint.introPeriodValue),
		periodNumber: 1,
		tags,
	}
}
